import React, { useEffect, useRef, useState } from 'react';
import './BuildBwobPage.css';
import { Link } from 'react-router-dom';
import { getTightAlphaBoundsFromImage } from './imageTightBounds';

function BuildBwobPage() {
  const canvasRef = useRef(null);
  const dragStateRef = useRef(null);
  const [selectedBackground, setSelectedBackground] = useState('vaporwave-background.webp');

  const [layers, setLayers] = useState([]);
  const [activeLayerId, setActiveLayerId] = useState(null);

  const getCanvasSize = () => {
    const el = canvasRef.current;
    if (!el) return { width: 0, height: 0 };
    const rect = el.getBoundingClientRect();
    return {
      width: Math.max(1, Math.floor(rect.width)),
      height: Math.max(1, Math.floor(rect.height))
    };
  };

  /** Allow positioning oversized layers beyond the frame (handles stay usable). */
  const clampLayerPos = (v) => Math.max(-2, Math.min(3, v));

  const MIN_LAYER_W = 0.03;

  const makeId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  };

  const backgrounds = [
    { id: 'vaporwave', name: 'Vaporwave', file: 'vaporwave-background.webp' },
    { id: 'liquid-metal', name: 'Liquid Metal', file: 'Liquid-Metal.webp' },
    { id: 'cyberpunk', name: 'Cyberpunk', file: 'cyberpunk-background.webp' },
    { id: 'grain', name: 'Grain', file: 'grain-background.webp' },
    { id: 'pastel-pink', name: 'Pastel Pink', file: 'pastel-pink-background.webp' },
    { id: 'default', name: 'Default', file: null }
  ];

  const hats = [
    { id: 'crown', name: 'Crown', file: 'crown.webp' },
    { id: 'wizard-hat', name: 'Wizard Hat', file: 'wizard-hat.webp' },
    { id: 'partyhat', name: 'Party Hat', file: 'partyhat.webp' },
    { id: 'sombrero-hat', name: 'Sombrero', file: 'sombrero-hat.webp' }
  ];

  const avatars = [
    { id: 'default', name: 'Default BWOB', file: 'bwob-base-sprite.png' },
    { id: 'gigachad', name: 'Gigachad BWOB', file: 'Gigachad-bwob.webp' }
  ];

  const companions = [
    { id: 'anime-girl', name: 'Anime girl', file: 'Mai-Bunny-Girl.png' }
  ];

  const avatarSrcForFile = (file) => (file && file.startsWith('Gigachad') ? `/avatars/${file}` : `/${file}`);
  const getCompanionSrc = (file) => (file && file.toLowerCase().endsWith('.png') ? `/${file}` : `/accessories/${file}`);

  const initialAvatarFile = 'bwob-base-sprite.png';

  useEffect(() => {
    setLayers((prev) => {
      if (prev.length > 0) return prev;

      const avatarLayer = {
        id: makeId(),
        kind: 'avatar',
        src: avatarSrcForFile(initialAvatarFile),
        x: 0.5,
        y: 0.55,
        w: 0.9,
        z: 1,
        tightBounds: null,
        naturalW: 0,
        naturalH: 0
      };
      return [avatarLayer];
    });
  }, []);

  const setAvatarFile = (file) => {
    const src = avatarSrcForFile(file);
    setLayers((prev) => {
      const avatarIndex = prev.findIndex((l) => l.kind === 'avatar');
      if (avatarIndex === -1) {
        const layer = {
          id: makeId(),
          kind: 'avatar',
          src,
          x: 0.5,
          y: 0.55,
          w: 0.78,
          z: Date.now(),
          tightBounds: null,
          naturalW: 0,
          naturalH: 0
        };
        setActiveLayerId(layer.id);
        return [...prev, layer];
      }
      const updated = prev.map((l, i) =>
        i === avatarIndex ? { ...l, src, tightBounds: null, naturalW: 0, naturalH: 0 } : l
      );
      setActiveLayerId(prev[avatarIndex].id);
      return updated;
    });
  };

  const removeLayer = (id) => {
    setLayers((prev) => prev.filter((l) => l.id !== id));
    if (activeLayerId === id) setActiveLayerId(null);
  };

  const removeLayersByKind = (kind) => {
    const currentActive = layers.find((l) => l.id === activeLayerId);
    setLayers((prev) => prev.filter((l) => l.kind !== kind));
    if (currentActive && currentActive.kind === kind) setActiveLayerId(null);
  };

  const layerWidthAsCanvasFrac = (layer) => {
    const tb = layer.tightBounds;
    const nw = layer.naturalW;
    if (tb && nw > 0) {
      return layer.w * ((tb.right - tb.left) / nw);
    }
    return layer.w;
  };

  const handleLayerImageLoad = (layerId) => (e) => {
    const el = e.currentTarget;
    const bounds = getTightAlphaBoundsFromImage(el);
    setLayers((prev) =>
      prev.map((l) =>
        l.id === layerId
          ? {
              ...l,
              naturalW: el.naturalWidth,
              naturalH: el.naturalHeight,
              tightBounds: bounds
            }
          : l
      )
    );
  };

  const toggleHatLayer = (hat) => {
    const src = `/accessories/${hat.file}`;
    const existing = layers.find((l) => l.kind === 'hat' && l.src === src);
    if (existing) {
      removeLayer(existing.id);
      return;
    }
    const id = makeId();
    setLayers((prev) => [
      ...prev.filter((l) => l.kind !== 'hat'),
      {
        id,
        kind: 'hat',
        src,
        x: 0.78,
        y: 0.22,
        w: 0.22,
        z: Date.now(),
        tightBounds: null,
        naturalW: 0,
        naturalH: 0
      }
    ]);
    setActiveLayerId(id);
  };

  const toggleCompanionLayer = (companion) => {
    const src = getCompanionSrc(companion.file);
    const existing = layers.find((l) => l.kind === 'companion' && l.src === src);
    if (existing) {
      removeLayer(existing.id);
      return;
    }
    const id = makeId();
    setLayers((prev) => [
      ...prev.filter((l) => l.kind !== 'companion'),
      {
        id,
        kind: 'companion',
        src,
        x: 0.78,
        y: 0.75,
        w: 0.45,
        z: Date.now(),
        tightBounds: null,
        naturalW: 0,
        naturalH: 0
      }
    ]);
    setActiveLayerId(id);
  };

  const beginDrag = (e, layerId) => {
    e.preventDefault();
    e.stopPropagation();

    const { width, height } = getCanvasSize();
    if (!width || !height) return;

    const layer = layers.find((l) => l.id === layerId);
    if (!layer) return;

    const pt = 'touches' in e ? e.touches[0] : e;
    const rect = canvasRef.current.getBoundingClientRect();
    const px = pt.clientX - rect.left;
    const py = pt.clientY - rect.top;

    dragStateRef.current = {
      mode: 'move',
      layerId,
      startX: px,
      startY: py,
      startLayerX: layer.x,
      startLayerY: layer.y,
      width,
      height
    };
    setActiveLayerId(layerId);
  };

  const beginResize = (e, layerId, handle) => {
    e.preventDefault();
    e.stopPropagation();

    const { width, height } = getCanvasSize();
    if (!width || !height) return;

    const layer = layers.find((l) => l.id === layerId);
    if (!layer) return;

    const pt = 'touches' in e ? e.touches[0] : e;
    const rect = canvasRef.current.getBoundingClientRect();
    const px = pt.clientX - rect.left;
    const py = pt.clientY - rect.top;

    dragStateRef.current = {
      mode: 'resize',
      layerId,
      handle,
      startX: px,
      startY: py,
      startLayerX: layer.x,
      startLayerY: layer.y,
      startW: layer.w,
      width,
      height
    };
    setActiveLayerId(layerId);
  };

  useEffect(() => {
    const onMove = (e) => {
      const st = dragStateRef.current;
      if (!st) return;
      const el = canvasRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const pt = e.touches && e.touches[0] ? e.touches[0] : e;
      const px = pt.clientX - rect.left;
      const py = pt.clientY - rect.top;

      if (st.mode === 'move') {
        const dx = (px - st.startX) / st.width;
        const dy = (py - st.startY) / st.height;
        setLayers((prev) =>
          prev.map((l) =>
            l.id === st.layerId
              ? { ...l, x: clampLayerPos(st.startLayerX + dx), y: clampLayerPos(st.startLayerY + dy) }
              : l
          )
        );
      }

      if (st.mode === 'resize') {
        const dxPx = px - st.startX;
        const dyPx = py - st.startY;

        const handle = st.handle || 'br';
        const usesHorizontal = handle.includes('l') || handle.includes('r');
        const usesVertical = handle.includes('t') || handle.includes('b');

        const horizontalChange = handle.includes('l') ? -dxPx : (handle.includes('r') ? dxPx : 0);
        const verticalChange = handle.includes('t') ? -dyPx : (handle.includes('b') ? dyPx : 0);

        let widthChangePx = 0;
        if (usesHorizontal && usesVertical) {
          widthChangePx = Math.abs(horizontalChange) >= Math.abs(verticalChange) ? horizontalChange : verticalChange;
        } else if (usesHorizontal) {
          widthChangePx = horizontalChange;
        } else if (usesVertical) {
          widthChangePx = verticalChange;
        }

        const rawW = st.startW + widthChangePx / st.width;
        const nextW = Math.max(MIN_LAYER_W, rawW);

        const nextX = clampLayerPos(st.startLayerX + (usesHorizontal ? dxPx / (2 * st.width) : 0));
        const nextY = clampLayerPos(st.startLayerY + (usesVertical ? dyPx / (2 * st.height) : 0));

        setLayers((prev) => prev.map((l) => (l.id === st.layerId ? { ...l, x: nextX, y: nextY, w: nextW } : l)));
      }
    };

    const onUp = () => {
      dragStateRef.current = null;
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchmove', onMove, { passive: false });
    window.addEventListener('touchend', onUp);

    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchmove', onMove);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  const handleDownloadImage = async () => {
    try {
      const { width, height } = getCanvasSize();
      if (!width || !height) return;

      const scale = 2; // export crisp
      const canvas = document.createElement('canvas');
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext('2d');
      ctx.scale(scale, scale);

      const loadImage = (src) => new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });

      const drawCover = (img) => {
        const scaleCover = Math.max(width / img.width, height / img.height);
        const dw = img.width * scaleCover;
        const dh = img.height * scaleCover;
        const dx = (width - dw) / 2;
        const dy = (height - dh) / 2;
        ctx.drawImage(img, dx, dy, dw, dh);
      };

      // Background
      if (selectedBackground) {
        const bgImg = await loadImage(`/backgrounds/${selectedBackground}`);
        drawCover(bgImg);
      } else {
        ctx.fillStyle = '#0b0b0b';
        ctx.fillRect(0, 0, width, height);
      }

      const orderedLayers = [...layers].sort((a, b) => (a.z ?? 0) - (b.z ?? 0));
      for (const layer of orderedLayers) {
        const img = await loadImage(layer.src);
        const tb = layer.tightBounds;
        const nw = img.naturalWidth;
        const nh = img.naturalHeight;
        const W = Math.max(1, layer.w * width);
        if (tb && nw > 0 && nh > 0) {
          const twPx = tb.right - tb.left;
          const thPx = tb.bottom - tb.top;
          const destW = W * (twPx / nw);
          const destH = W * (thPx / nw);
          const dx = layer.x * width - destW / 2;
          const dy = layer.y * height - destH / 2;
          ctx.drawImage(img, tb.left, tb.top, twPx, thPx, dx, dy, destW, destH);
        } else {
          const w = Math.max(1, layer.w * width);
          const h = img.height * (w / img.width);
          const x = layer.x * width - w / 2;
          const y = layer.y * height - h / 2;
          ctx.drawImage(img, x, y, w, h);
        }
      }

      const url = canvas.toDataURL('image/png');
      const a = document.createElement('a');
      a.href = url;
      a.download = 'BWOB.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      // no-op: fail silently
    }
  };

  return (
    <div className="build-bwob-container">
      <div className="build-bwob-header">
        <Link to="/" className="back-button">Back</Link>
        <h1 className="build-title">Build a BWOB</h1>
      </div>

      <div className="build-bwob-content">
        <div className="grain-overlay" style={{ backgroundImage: "url(/backgrounds/grain-background.webp)", backgroundSize: 'cover' }} />
        <div className="bwob-preview">
          <div
            className="bwob-canvas"
            ref={canvasRef}
            onMouseDown={() => setActiveLayerId(null)}
            onTouchStart={() => setActiveLayerId(null)}
          >
            <div
              className="bwob-canvas-bg"
              aria-hidden
              style={{
                backgroundImage: selectedBackground ? `url(/backgrounds/${selectedBackground})` : 'none'
              }}
            />
            <div className="bwob-canvas-clip">
              <div className="bwob-canvas-stage">
              {[...layers]
                .sort((a, b) => (a.z ?? 0) - (b.z ?? 0))
                .map((layer) => {
                  const isActive = layer.id === activeLayerId;
                  const tb = layer.tightBounds;
                  const nw = layer.naturalW;
                  const hasCrop = tb && nw > 0;
                  const twPx = hasCrop ? tb.right - tb.left : 0;
                  const thPx = hasCrop ? tb.bottom - tb.top : 0;
                  const widthFrac = layerWidthAsCanvasFrac(layer);
                  return (
                    <div
                      key={layer.id}
                      className={`bwob-layer ${isActive ? 'active' : ''}`}
                      style={{
                        left: `${layer.x * 100}%`,
                        top: `${layer.y * 100}%`,
                        width: `${widthFrac * 100}%`
                      }}
                      onMouseDown={(e) => beginDrag(e, layer.id)}
                      onTouchStart={(e) => beginDrag(e, layer.id)}
                    >
                      <div
                        className="bwob-layer-crop"
                        style={hasCrop ? { aspectRatio: `${twPx} / ${thPx}` } : undefined}
                      >
                        <img
                          src={layer.src}
                          alt={layer.kind}
                          draggable={false}
                          onLoad={handleLayerImageLoad(layer.id)}
                          style={
                            hasCrop
                              ? {
                                  position: 'absolute',
                                  left: `${(-tb.left / twPx) * 100}%`,
                                  top: `${(-tb.top / thPx) * 100}%`,
                                  width: `${(nw / twPx) * 100}%`,
                                  height: `${(layer.naturalH / thPx) * 100}%`,
                                  maxWidth: 'none',
                                  display: 'block'
                                }
                              : {
                                  width: '100%',
                                  height: 'auto',
                                  display: 'block'
                                }
                          }
                        />
                      </div>
                      {isActive && (
                        <>
                          <div
                            className="bwob-layer-handle br"
                            onMouseDown={(e) => beginResize(e, layer.id, 'br')}
                            onTouchStart={(e) => beginResize(e, layer.id, 'br')}
                          />
                          <div
                            className="bwob-layer-handle bl"
                            onMouseDown={(e) => beginResize(e, layer.id, 'bl')}
                            onTouchStart={(e) => beginResize(e, layer.id, 'bl')}
                          />
                          <div
                            className="bwob-layer-handle tr"
                            onMouseDown={(e) => beginResize(e, layer.id, 'tr')}
                            onTouchStart={(e) => beginResize(e, layer.id, 'tr')}
                          />
                          <div
                            className="bwob-layer-handle tl"
                            onMouseDown={(e) => beginResize(e, layer.id, 'tl')}
                            onTouchStart={(e) => beginResize(e, layer.id, 'tl')}
                          />
                          <div
                            className="bwob-layer-handle t"
                            onMouseDown={(e) => beginResize(e, layer.id, 't')}
                            onTouchStart={(e) => beginResize(e, layer.id, 't')}
                          />
                          <div
                            className="bwob-layer-handle b"
                            onMouseDown={(e) => beginResize(e, layer.id, 'b')}
                            onTouchStart={(e) => beginResize(e, layer.id, 'b')}
                          />
                          <div
                            className="bwob-layer-handle l"
                            onMouseDown={(e) => beginResize(e, layer.id, 'l')}
                            onTouchStart={(e) => beginResize(e, layer.id, 'l')}
                          />
                          <div
                            className="bwob-layer-handle r"
                            onMouseDown={(e) => beginResize(e, layer.id, 'r')}
                            onTouchStart={(e) => beginResize(e, layer.id, 'r')}
                          />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="download-btn-container">
            <button className="download-btn" onClick={handleDownloadImage}>Download PNG</button>
          </div>
        </div>

        <div className="customization-panel">
          <div className="option-section">
            <div className="section-header">
              <h3>Backgrounds</h3>
              <button className="close-btn">×</button>
            </div>
            <div className="options-grid">
              {backgrounds.map((bg) => (
                <div 
                  key={bg.id}
                  className={`option-item ${selectedBackground === bg.file ? 'selected' : ''}`}
                  onClick={() => setSelectedBackground(bg.file)}
                >
                  <div 
                    className="option-preview"
                    style={{
                      backgroundImage: bg.file ? `url(/backgrounds/${bg.file})` : 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                      backgroundSize: bg.file ? 'cover' : '10px 10px',
                      backgroundPosition: bg.file ? 'center' : '0 0, 0 5px, 5px -5px, -5px 0px'
                    }}
                  />
                  <span className="option-name">{bg.name}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="option-section">
            <div className="section-header hats-header">
              <h3>Hats</h3>
              <button className="close-btn">×</button>
            </div>
            <div className="options-grid">
              <div 
                className={`option-item ${!layers.some((l) => l.kind === 'hat') ? 'selected' : ''}`}
                onClick={() => removeLayersByKind('hat')}
              >
                <div className="option-preview no-accessory">None</div>
                <span className="option-name">None</span>
              </div>
              {hats.map((hat) => {
                const hatSrc = `/accessories/${hat.file}`;
                return (
                <div 
                  key={hat.id}
                  className={`option-item ${layers.some((l) => l.kind === 'hat' && l.src === hatSrc) ? 'selected' : ''}`}
                  onClick={() => toggleHatLayer(hat)}
                >
                  <div className="option-preview">
                    <img src={hatSrc} alt={hat.name} />
                  </div>
                  <span className="option-name">{hat.name}</span>
                </div>
              );})}
            </div>
          </div>

          <div className="option-section">
            <div className="section-header avatars-header">
              <h3>Avatars</h3>
              <button className="close-btn">×</button>
            </div>
            <div className="options-grid">
              {avatars.map((avatar) => (
                <div 
                  key={avatar.id}
                  className="option-item"
                  onClick={() => setAvatarFile(avatar.file)}
                >
                  <div className="option-preview">
                    <img src={avatar.file.startsWith('Gigachad') ? `/avatars/${avatar.file}` : `/${avatar.file}`} alt={avatar.name} />
                  </div>
                  <span className="option-name">{avatar.name}</span>
                </div>
              ))}
              <div className="option-item coming-soon" aria-disabled="true">
                <div className="option-preview no-accessory">⋯</div>
                <span className="option-name">More coming soon</span>
              </div>
            </div>
            <p className="options-soon-foot">More avatars coming soon.</p>
          </div>

          <div className="option-section">
            <div className="section-header companions-header">
              <h3>Companions</h3>
              <button className="close-btn">×</button>
            </div>
            <div className="options-grid">
              <div 
                className={`option-item ${!layers.some((l) => l.kind === 'companion') ? 'selected' : ''}`}
                onClick={() => removeLayersByKind('companion')}
              >
                <div className="option-preview no-accessory">None</div>
                <span className="option-name">None</span>
              </div>
              {companions.map((companion) => {
                const cSrc = getCompanionSrc(companion.file);
                return (
                <div 
                  key={companion.id}
                  className={`option-item ${layers.some((l) => l.kind === 'companion' && l.src === cSrc) ? 'selected' : ''}`}
                  onClick={() => toggleCompanionLayer(companion)}
                >
                  <div className="option-preview">
                    <img src={cSrc} alt={companion.name} />
                  </div>
                  <span className="option-name">{companion.name}</span>
                </div>
              );})}
              <div className="option-item coming-soon" aria-disabled="true">
                <div className="option-preview no-accessory">⋯</div>
                <span className="option-name">More coming soon</span>
              </div>
            </div>
            <p className="options-soon-foot">More companions coming soon.</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default BuildBwobPage;
